import { useState } from "react";

type Platform = "tiktok" | "reels" | "shorts";

const PLATFORM_CONFIG: Record<Platform, { label: string; icon: string; color: string }> = {
  tiktok: { label: "TikTok", icon: "ri-tiktok-line", color: "#FF2D55" },
  reels: { label: "Reels", icon: "ri-instagram-line", color: "#E1306C" },
  shorts: { label: "Shorts", icon: "ri-youtube-line", color: "#FF0000" },
};

const SUBTITLES = [
  { id: 1, time: "0:00", text: "종교에서 어떻게 알고 나왔어요?" },
  { id: 2, time: "0:03", text: "먼저 알게 된 계기는" },
  { id: 3, time: "0:07", text: "유튜브에서 본 종교의 실체" },
  { id: 4, time: "0:10", text: "알고나서는 쉽게 나올 수 있었어?" },
];

export default function VideoPreview() {
  const [isPlaying, setIsPlaying] = useState(false);
  const [platform, setPlatform] = useState<Platform>("tiktok");
  const [showSafeArea, setShowSafeArea] = useState(true);
  const [editingSubtitle, setEditingSubtitle] = useState<number | null>(null);
  const [subtitles, setSubtitles] = useState(SUBTITLES);

  return (
    <div className="flex flex-col h-full">
      {/* Platform Toggle */}
      <div className="flex items-center justify-between mb-3">
        <span className="text-gray-400 text-xs font-medium" style={{ fontFamily: "'Inter', sans-serif" }}>
          플랫폼 미리보기
        </span>
        <div className="flex items-center gap-1 bg-[#0A0E14] rounded-lg p-1 border border-white/8">
          {(Object.keys(PLATFORM_CONFIG) as Platform[]).map((p) => (
            <button
              key={p}
              onClick={() => setPlatform(p)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                platform === p
                  ? "bg-white/10 text-white"
                  : "text-gray-500 hover:text-gray-300"
              }`}
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              <span className="w-3 h-3 flex items-center justify-center">
                <i className={PLATFORM_CONFIG[p].icon} style={{ color: platform === p ? PLATFORM_CONFIG[p].color : undefined }} />
              </span>
              {PLATFORM_CONFIG[p].label}
            </button>
          ))}
        </div>
        <button
          onClick={() => setShowSafeArea(!showSafeArea)}
          className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border transition-all cursor-pointer whitespace-nowrap ${
            showSafeArea
              ? "border-[#00D9FF]/40 bg-[#00D9FF]/8 text-[#00D9FF]"
              : "border-white/8 bg-white/4 text-gray-500 hover:text-gray-300"
          }`}
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          <span className="w-3 h-3 flex items-center justify-center">
            <i className="ri-layout-grid-line" />
          </span>
          안전 영역
        </button>
      </div>

      {/* Video Container - 9:16 aspect */}
      <div className="flex-1 flex items-center justify-center">
        <div
          className="relative bg-[#050709] rounded-2xl overflow-hidden border border-white/10"
          style={{ aspectRatio: "9/16", height: "100%", maxHeight: "520px" }}
        >
          {/* Video thumbnail */}
          <img
            src="https://static.readdy.ai/image/7e3352e848b93c3eb9a2ea481cb871cb/2af2b4c3df902c350c994245c41ee4f6.png"
            alt="Preview"
            className="w-full h-full object-cover object-top"
          />

          {/* Dark overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20" />

          {/* Safe area guides */}
          {showSafeArea && (
            <>
              <div className="absolute inset-0 border-2 border-white/10 rounded-xl pointer-events-none m-3" />
              <div className="absolute top-3 left-1/2 -translate-x-1/2 text-white/20 text-xs" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                안전 영역
              </div>
              {/* Top safe area */}
              <div className="absolute top-0 left-0 right-0 h-16 border-b border-dashed border-white/10 pointer-events-none" />
              {/* Bottom safe area */}
              <div className="absolute bottom-0 left-0 right-0 h-24 border-t border-dashed border-white/10 pointer-events-none" />
            </>
          )}

          {/* Hook badge */}
          <div
            className="absolute top-5 left-1/2 -translate-x-1/2 flex items-center gap-1.5 px-3 py-1 rounded-full border border-[#F59E0B]/50 text-xs font-bold"
            style={{ background: "rgba(245,158,11,0.15)", color: "#F59E0B", fontFamily: "'JetBrains Mono', monospace" }}
          >
            <i className="ri-flashlight-line" />
            HOOK 구간 · 0:00–0:03
          </div>

          {/* Play/Pause */}
          <div
            className="absolute inset-0 flex items-center justify-center cursor-pointer"
            onClick={() => setIsPlaying(!isPlaying)}
          >
            {!isPlaying && (
              <div className="w-14 h-14 flex items-center justify-center rounded-full bg-white/10 backdrop-blur-sm border border-white/25 hover:bg-white/20 transition-all">
                <i className="ri-play-fill text-white text-2xl ml-1" />
              </div>
            )}
          </div>

          {/* Active subtitle overlay */}
          <div className="absolute bottom-28 left-3 right-3">
            {editingSubtitle !== null ? (
              <div className="bg-black/70 rounded-xl p-2">
                <input
                  autoFocus
                  value={subtitles.find((s) => s.id === editingSubtitle)?.text || ""}
                  onChange={(e) => setSubtitles((prev) => prev.map((s) => s.id === editingSubtitle ? { ...s, text: e.target.value } : s))}
                  onBlur={() => setEditingSubtitle(null)}
                  className="w-full bg-transparent text-white text-center text-sm font-bold outline-none"
                  style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                />
              </div>
            ) : (
              <div
                className="text-center cursor-text group"
                onClick={() => setEditingSubtitle(subtitles[0].id)}
              >
                <span
                  className="inline-block bg-black/75 backdrop-blur-sm text-white text-sm font-bold px-3 py-1.5 rounded-lg leading-tight group-hover:ring-1 group-hover:ring-[#00D9FF]/60 transition-all"
                  style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                >
                  {subtitles[0].text}
                </span>
              </div>
            )}
          </div>

          {/* Progress bar */}
          <div className="absolute bottom-3 left-3 right-3">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-white/60 text-xs" style={{ fontFamily: "'JetBrains Mono', monospace" }}>0:14</span>
              <div className="flex-1 h-1 bg-white/15 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full"
                  style={{ width: "30%", background: "linear-gradient(90deg, #00D9FF, #10B981)" }}
                />
              </div>
              <span className="text-white/60 text-xs" style={{ fontFamily: "'JetBrains Mono', monospace" }}>0:47</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
